import { useHead } from '#imports';
import type { RouteLocationNormalizedLoadedGeneric } from '#vue-router';
import useImageParams from '@/composables/useImageParams';
import { getFileExtension, isVideo } from '@/utils/helpers';

export const useMetaCollectionSlug = ({ collectionProfile, route }: {
    collectionProfile: AppWTFCollection;
    route: RouteLocationNormalizedLoadedGeneric;
}) => {
    const contentWidth = 1200;
    const contentHeight = 630;

    const defaultImg = '/og_1200x630.jpg';
    const defaultExt = 'image/jpg';

    const contentUrl = collectionProfile?.bannerImageUrl && isVideo(collectionProfile.bannerImageUrl)
        ? collectionProfile.bannerImageUrl
        : !collectionProfile.imageUrl
                ? defaultImg
                : useImageParams(
                        collectionProfile.imageUrl,
                        contentWidth
                    );

    const pathWithoutParams = import.meta.env.VITE_DOMEN_URL + route.path;
    const pathWithParams = import.meta.env.VITE_DOMEN_URL + route.fullPath;

    const imgTags = [
        {
            property: 'og:image:type',
            content: contentUrl === defaultImg
                ? defaultExt
                : `image/${getFileExtension(contentUrl)}`
        },
        {
            property: 'og:image:width',
            content: `${contentWidth}`
        },
        {
            property: 'og:image:height',
            content: `${contentHeight}`
        },
        {
            property: 'og:image',
            content: contentUrl
        },
        {
            property: 'og:image:alt',
            content: collectionProfile.name
        },
        {
            name: 'twitter:image',
            content: contentUrl
        }
    ];

    const videoTags = [
        {
            property: 'og:video:type',
            content: `video/${getFileExtension(contentUrl)}`
        },
        {
            property: 'og:video:width',
            content: `${contentWidth}`
        },
        {
            property: 'og:video:height',
            content: `${contentHeight}`
        },
        {
            property: 'og:video',
            content: contentUrl
        },
        {
            property: 'twitter:card',
            content: 'player'
        },
        {
            property: 'twitter:player',
            content: import.meta.env.VITE_DOMEN_URL
        },
        {
            property: 'twitter:player:stream:content_type',
            content: `video/${getFileExtension(contentUrl)}`
        },
        {
            property: 'twitter:player:width',
            content: `${contentWidth}`
        },
        {
            property: 'twitter:player:height',
            content: `${contentHeight}`
        }

    ];

    useHead({
        title: collectionProfile.name,
        meta: [
            {
                name: 'description',
                content: collectionProfile.description
            },
            {
                name: 'keywords',
                content: `${collectionProfile?.name.toLowerCase() || ''} background image, icon, collection name, collection Items, collection owners, floor price, 1D Volume`
            },
            {
                property: 'og:title',
                content: collectionProfile.name
            },
            {
                property: 'og:description',
                content: collectionProfile.description
            },
            {
                name: 'twitter:title',
                content: collectionProfile.name
            },
            {
                name: 'twitter:card',
                content: 'summary_large_image'
            },
            {
                name: 'twitter:url',
                content: pathWithoutParams
            },
            {
                name: 'twitter:creator',
                content: collectionProfile.owner
            },
            {
                name: 'twitter:description',
                content: collectionProfile.description
            },
            {
                name: 'twitter:image',
                content: contentUrl
            },
            ...collectionProfile?.bannerImageUrl && isVideo(collectionProfile.bannerImageUrl)
                ? videoTags
                : imgTags
        ],
        link: [
            {
                rel: 'canonical',
                href: pathWithParams
            },
            {
                rel: 'preconnect',
                href: pathWithParams
            },
            {
                rel: 'dns-prefetch',
                href: pathWithParams
            }
        ]
    });
};
